import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  ChevronRight,
  Flame,
  LoaderCircle,
  ShieldAlert,
  Sparkles,
  Trophy,
  Users,
} from "lucide-react";
import AdminOnly from "../../../components/guards/AdminOnly";
import UserAvatar from "../../../components/ui/UserAvatar";
import AdminKpiCard from "../components/AdminKpiCard";
import AdminSectionCard from "../components/AdminSectionCard";
import { approveUserAccess, inviteMemberByEmail } from "../services/access";
import { getInsightsSummary } from "../services/insights";
import UserTopNav from "../../../components/layout/UserTopNav";

type Summary = Awaited<ReturnType<typeof getInsightsSummary>>;

function RankBadge({ index }: { index: number }) {
  const rank = index + 1;

  const styles =
    rank === 1
      ? "border-[color:rgba(136,156,255,0.28)] bg-[color:rgba(59,84,170,0.18)] text-[color:#dbe6ff]"
      : rank === 2
      ? "border-neutral-300/20 bg-neutral-300/10 text-neutral-200"
      : rank === 3
      ? "border-sky-500/20 bg-sky-500/10 text-sky-200"
      : "border-white/10 bg-white/[0.04] text-white/60";

  return (
    <div
      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold sm:h-9 sm:w-9 ${styles}`}
    >
      {rank}
    </div>
  );
}

function getAttentionBadge(lastCheckInDate?: string) {
  if (!lastCheckInDate) {
    return "border-red-500/25 bg-red-500/10 text-red-300";
  }

  const last = new Date(`${lastCheckInDate}T00:00:00`);
  const now = new Date();
  const diff = now.getTime() - last.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days > 30) {
    return "border-red-500/25 bg-red-500/10 text-red-300";
  }

  return "border-[color:rgba(136,156,255,0.24)] bg-[color:rgba(59,84,170,0.18)] text-[color:#dbe6ff]";
}

function getBillingBadge(user: Summary["users"][number]) {
  if (user.billingStatus === "active" || user.billingStatus === "trialing") {
    return {
      label: user.billingStatus === "trialing" ? "Trialing" : "Paid",
      className: "border-emerald-500/25 bg-emerald-500/10 text-emerald-200",
    };
  }

  if (user.billingStatus === "past_due" || user.billingStatus === "incomplete") {
    return {
      label: "Payment issue",
      className: "border-[color:rgba(136,156,255,0.24)] bg-[color:rgba(59,84,170,0.18)] text-[color:#dbe6ff]",
    };
  }

  if (user.appAccessStatus === "full" || user.appAccessStatus === "limited") {
    return {
      label: "Manual access",
      className: "border-sky-500/25 bg-sky-500/10 text-sky-200",
    };
  }

  return {
    label: "Unpaid",
    className: "border-white/10 bg-white/[0.04] text-white/70",
  };
}

export default function AdminInsights() {
  const [data, setData] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [approvingUserId, setApprovingUserId] = useState<string | null>(null);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [memberFilter, setMemberFilter] = useState<
    "all" | "paid" | "payment_issue" | "manual_access" | "unpaid"
  >("all");
  const [memberSearch, setMemberSearch] = useState("");

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const res = await getInsightsSummary();
        if (alive) setData(res);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const filteredMembers = useMemo(() => {
    if (!data) return [];

    const query = memberSearch.trim().toLowerCase();

    return data.users
      .filter((user) => {
        if (!query) return true;
        return (
          (user.name || "").toLowerCase().includes(query) ||
          (user.email || "").toLowerCase().includes(query)
        );
      })
      .filter((user) => {
        const isPaid = user.billingStatus === "active" || user.billingStatus === "trialing";
        const hasPaymentIssue =
          user.billingStatus === "past_due" || user.billingStatus === "incomplete";
        const hasManualAccess =
          !isPaid &&
          !hasPaymentIssue &&
          (user.appAccessStatus === "full" || user.appAccessStatus === "limited");

        if (memberFilter === "paid") return isPaid;
        if (memberFilter === "payment_issue") return hasPaymentIssue;
        if (memberFilter === "manual_access") return hasManualAccess;
        if (memberFilter === "unpaid") return !isPaid && !hasPaymentIssue && !hasManualAccess;

        return true;
      })
      .sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""));
  }, [data, memberFilter, memberSearch]);

  async function handleApprove(userId: string) {
    try {
      setApprovingUserId(userId);
      setApprovalError(null);
      await approveUserAccess(userId);
      setData((current) => {
        if (!current) return current;
        const approvedUser =
          current.pendingApprovals.find((user) => user.id === userId) ?? null;
        const nextPending = current.pendingApprovals.filter((user) => user.id !== userId);

        return {
          ...current,
          totalMembers: current.totalMembers + (approvedUser ? 1 : 0),
          pendingApprovals: nextPending,
          users: approvedUser
            ? [...current.users, { ...approvedUser, approvalStatus: "approved" }]
            : current.users,
        };
      });
    } catch (err: any) {
      setApprovalError(err?.message ?? "Failed to approve member.");
    } finally {
      setApprovingUserId(null);
    }
  }

  async function handleInviteSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    try {
      setInviteLoading(true);
      setInviteError(null);
      setInviteSuccess(null);
      const email = inviteEmail.trim().toLowerCase();

      await inviteMemberByEmail(email);

      setInviteSuccess(`Invite sent to ${email}.`);
      setInviteEmail("");
    } catch (err: any) {
      setInviteError(err?.message ?? "Failed to send invite email.");
    } finally {
      setInviteLoading(false);
    }
  }

  return (
    <AdminOnly>
      <div className="min-h-screen bg-black text-white">
        <UserTopNav />

        <div className="px-3 py-5 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            {loading ? (
              <div className="mt-6 rounded-3xl border border-white/10 bg-neutral-950 p-6 text-neutral-400 sm:mt-8">
                Loading insights...
              </div>
            ) : !data ? (
              <div className="mt-6 rounded-3xl border border-white/10 bg-neutral-950 p-6 text-neutral-400 sm:mt-8">
                No data available.
              </div>
            ) : (
              <>
                <section className="relative overflow-hidden rounded-[1.9rem] border border-[color:rgba(136,156,255,0.12)] bg-[radial-gradient(circle_at_top_left,rgba(58,84,168,0.18),transparent_30%),linear-gradient(180deg,rgba(12,14,22,0.98),rgba(8,9,14,0.98))] p-4 sm:rounded-[2rem] sm:p-8">
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[rgba(142,163,255,0.45)] to-transparent" />

                  <div className="relative z-10 grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.85fr)]">
                    <div className="min-w-0">
                      <div className="inline-flex items-center gap-2 rounded-full border border-[color:rgba(136,156,255,0.18)] bg-[color:rgba(59,84,170,0.18)] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-[color:#dbe6ff] sm:text-[11px] sm:tracking-[0.28em]">
                        <Sparkles className="h-3.5 w-3.5 shrink-0" />
                        Admin Insights
                      </div>

                      <h1 className="mt-4 text-3xl font-heading tracking-tight sm:text-5xl">
                        Member Pulse
                      </h1>

                      <p className="mt-3 max-w-2xl text-sm leading-6 text-neutral-400 sm:text-base">
                        See who is active, who needs attention, and where revenue and engagement
                        risk is sitting across your member base.
                      </p>

                      <div className="mt-6 grid gap-3 sm:grid-cols-3">
                        <div className="rounded-2xl border border-[color:rgba(136,156,255,0.12)] bg-[linear-gradient(180deg,rgba(18,23,38,0.88),rgba(10,12,18,0.72))] p-4">
                          <div className="text-[10px] uppercase tracking-[0.2em] text-[color:#aabaff]">
                            Top momentum
                          </div>
                          <div className="mt-3 text-xl font-semibold text-white">
                            {data.topStreakUser?.name || "No streak leader yet"}
                          </div>
                          <p className="mt-2 text-sm text-neutral-400">
                            {data.topStreakUser
                              ? `${data.topStreakUser.stats?.currentStreak ?? 0} week streak leading the room.`
                              : "Once members begin checking in consistently, the streak leader will show here."}
                          </p>
                        </div>

                        <div className="rounded-2xl border border-[color:rgba(136,156,255,0.12)] bg-[linear-gradient(180deg,rgba(18,23,38,0.88),rgba(10,12,18,0.72))] p-4">
                          <div className="text-[10px] uppercase tracking-[0.2em] text-[color:#aabaff]">
                            Billing risk
                          </div>
                          <div className="mt-3 text-xl font-semibold text-white">
                            {data.paymentAttentionMembers} members
                          </div>
                          <p className="mt-2 text-sm text-neutral-400">
                            Payment issues surfaced before they turn into churn or access confusion.
                          </p>
                        </div>

                        <div className="rounded-2xl border border-[color:rgba(136,156,255,0.12)] bg-[linear-gradient(180deg,rgba(18,23,38,0.88),rgba(10,12,18,0.72))] p-4">
                          <div className="text-[10px] uppercase tracking-[0.2em] text-[color:#aabaff]">
                            Access queue
                          </div>
                          <div className="mt-3 text-xl font-semibold text-white">
                            {data.pendingApprovals.length} waiting
                          </div>
                          <p className="mt-2 text-sm text-neutral-400">
                            Members needing manual unlock before they can fully use the app.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-2">
                      <div className="rounded-2xl border border-white/10 bg-black/35 p-4 backdrop-blur">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                          Members
                        </div>
                        <div className="mt-2 text-3xl font-semibold text-white">{data.totalMembers}</div>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-black/35 p-4 backdrop-blur">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                          Active billing
                        </div>
                        <div className="mt-2 text-3xl font-semibold text-white">{data.activeBillingMembers}</div>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-black/35 p-4 backdrop-blur">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                          Check-ins this month
                        </div>
                        <div className="mt-2 text-3xl font-semibold text-white">{data.monthCheckIns}</div>
                        <div className="mt-1 text-xs uppercase tracking-[0.18em] text-neutral-500">
                          {data.monthKey}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-black/35 p-4 backdrop-blur">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                          Active members
                        </div>
                        <div className="mt-2 text-3xl font-semibold text-white">{data.activeMembers}</div>
                      </div>
                    </div>
                  </div>
                </section>

                <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4 [&>*]:min-w-0">
                  <AdminKpiCard label="Total Members" value={data.totalMembers} />
                  <AdminKpiCard label="Paid Members" value={data.activeBillingMembers} />
                  <AdminKpiCard label="Manual Access" value={data.manualAccessMembers} />
                  <AdminKpiCard label="Billing Issues" value={data.paymentAttentionMembers} />
                </div>

                <div className="mt-6 grid gap-6 lg:mt-8 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)] [&>*]:min-w-0">
                  <AdminSectionCard title="Member Operations">
                    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
                      <form onSubmit={handleInviteSubmit} className="space-y-4">
                        <div>
                          <label className="mb-2 block text-sm font-medium text-neutral-300">
                            Invite a new member
                          </label>
                          <p className="mb-4 max-w-xl text-sm leading-6 text-neutral-400">
                            Send a sign-up link so the member can create their account and move into
                            billing and access setup.
                          </p>
                          <div className="flex flex-col gap-3 sm:flex-row">
                            <input
                              type="email"
                              value={inviteEmail}
                              onChange={(e) => setInviteEmail(e.target.value)}
                              placeholder="newmember@example.com"
                              autoComplete="email"
                              inputMode="email"
                              required
                              className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white outline-none transition placeholder:text-neutral-500 focus:border-[color:rgba(136,156,255,0.28)] focus:bg-white/[0.06]"
                            />
                            <button
                              type="submit"
                              disabled={inviteLoading}
                              className="rounded-2xl bg-[linear-gradient(135deg,#89a1ff,#4b63c9)] px-5 py-3 font-semibold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-70"
                            >
                              {inviteLoading ? "Sending..." : "Send invite"}
                            </button>
                          </div>
                        </div>

                        {inviteSuccess ? (
                          <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
                            {inviteSuccess}
                          </div>
                        ) : null}

                        {inviteError ? (
                          <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                            {inviteError}
                          </div>
                        ) : null}
                      </form>

                      <div className="rounded-2xl border border-[color:rgba(136,156,255,0.12)] bg-[linear-gradient(180deg,rgba(18,23,38,0.64),rgba(10,12,18,0.52))] p-4">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:#aabaff]">
                          Current read
                        </div>
                        <p className="mt-3 text-sm leading-6 text-neutral-300">
                          Attendance looks strongest at the top of this month&apos;s board, while
                          billing and inactivity flags show exactly where your follow-up should go next.
                        </p>
                        <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[color:#dbe6ff]">
                          Operator view
                          <ArrowUpRight className="h-4 w-4" />
                        </div>
                      </div>
                    </div>
                  </AdminSectionCard>

                  <AdminSectionCard title="Manual Access Queue">
                    {approvalError ? (
                      <div className="mb-4 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                        {approvalError}
                      </div>
                    ) : null}

                    {data.pendingApprovals.length === 0 ? (
                      <div className="rounded-2xl border border-emerald-500/15 bg-emerald-500/5 px-4 py-5 text-sm text-emerald-300">
                        No manual access unlocks are waiting right now.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {data.pendingApprovals.map((user) => {
                          const isApproving = approvingUserId === user.id;

                          return (
                            <div
                              key={user.id}
                              className="flex flex-col gap-4 rounded-2xl border border-white/8 bg-white/[0.02] px-4 py-4 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <div className="flex min-w-0 items-center gap-3">
                                <UserAvatar
                                  name={user.name || "Pending member"}
                                  photoURL={user.photoURL}
                                  size={44}
                                />

                                <div className="min-w-0">
                                  <div className="truncate text-sm font-medium text-white sm:text-base">
                                    {user.name || "Unnamed member"}
                                  </div>
                                  <div className="truncate text-xs text-neutral-500 sm:text-sm">
                                    {user.email || "No email"}
                                  </div>
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={() => handleApprove(user.id)}
                                disabled={isApproving}
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-100 transition hover:bg-emerald-500/15 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {isApproving ? (
                                  <LoaderCircle className="h-4 w-4 animate-spin" />
                                ) : (
                                  <CheckCircle2 className="h-4 w-4" />
                                )}
                                {isApproving ? "Unlocking..." : "Grant access"}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </AdminSectionCard>
                </div>

                <div className="mt-6 grid gap-6 lg:mt-8 lg:grid-cols-2 [&>*]:min-w-0">
                  <AdminSectionCard title="Top Attenders This Month">
                    <div className="space-y-3">
                      {data.topAttenders.length === 0 ? (
                        <div className="text-sm text-neutral-400">No member activity yet.</div>
                      ) : (
                        data.topAttenders.map((user, index) => (
                          <div
                            key={user.id}
                            className="group w-full overflow-hidden rounded-2xl border border-white/8 bg-white/[0.02] px-3 py-3 transition hover:border-[color:rgba(136,156,255,0.20)] hover:bg-white/[0.04] sm:px-4 sm:py-4"
                          >
                            <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                              <RankBadge index={index} />

                              <div className="relative shrink-0">
                                <UserAvatar
                                  name={user.name || "Member"}
                                  photoURL={user.photoURL}
                                  size={44}
                                />
                                {index === 0 ? (
                                  <div className="absolute -right-1 -top-1 rounded-full border border-[color:rgba(136,156,255,0.28)] bg-[color:rgba(59,84,170,0.18)] p-1 text-[color:#dbe6ff]">
                                    <Trophy className="h-3 w-3" />
                                  </div>
                                ) : null}
                              </div>

                              <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-medium text-white transition group-hover:text-[color:#dbe6ff] sm:text-base">
                                  {user.name || "Unnamed member"}
                                </div>
                                <div className="truncate text-xs text-neutral-500 sm:text-sm">
                                  {user.email || "No email"}
                                </div>
                                <div className="mt-2">
                                  <span
                                    className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${getBillingBadge(
                                      user
                                    ).className}`}
                                  >
                                    {getBillingBadge(user).label}
                                  </span>
                                </div>
                              </div>

                              <div className="shrink-0 pl-1 text-right sm:pl-2">
                                <div className="text-base font-semibold text-white sm:text-lg">
                                  {user.stats?.monthCheckIns?.[data.monthKey] ?? 0}
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 sm:text-[11px] sm:tracking-[0.2em]">
                                  check-ins
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </AdminSectionCard>

                  <AdminSectionCard title="Members Needing Attention">
                    {data.inactiveMembers.length === 0 ? (
                      <div className="rounded-2xl border border-emerald-500/15 bg-emerald-500/5 px-4 py-5 text-sm text-emerald-300">
                        No inactive members right now.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {data.inactiveMembers.map((user) => (
                          <div
                            key={user.id}
                            className="group w-full overflow-hidden rounded-2xl border border-white/8 bg-white/[0.02] px-3 py-3 transition hover:border-[color:rgba(136,156,255,0.20)] hover:bg-white/[0.04] sm:px-4 sm:py-4"
                          >
                            <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                              <div className="relative shrink-0">
                                <UserAvatar
                                  name={user.name || "Member"}
                                  photoURL={user.photoURL}
                                  size={44}
                                />
                                <div className="absolute -right-1 -top-1 rounded-full border border-red-500/25 bg-red-500/10 p-1 text-red-300">
                                  <ShieldAlert className="h-3 w-3" />
                                </div>
                              </div>

                              <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-medium text-white transition group-hover:text-[color:#dbe6ff] sm:text-base">
                                  {user.name || "Unnamed member"}
                                </div>
                                <div className="mt-1 flex min-w-0 items-center gap-2 text-xs text-neutral-500 sm:text-sm">
                                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                  <span className="truncate">
                                    Last check-in: {user.stats?.lastCheckInDate || "Never"}
                                  </span>
                                </div>
                                <div className="mt-2">
                                  <span
                                    className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${getBillingBadge(
                                      user
                                    ).className}`}
                                  >
                                    {getBillingBadge(user).label}
                                  </span>
                                </div>
                              </div>

                              <div className="hidden shrink-0 items-center gap-3 sm:flex">
                                <div
                                  className={`rounded-full border px-3 py-1 text-xs font-medium ${getAttentionBadge(
                                    user.stats?.lastCheckInDate
                                  )}`}
                                >
                                  Needs attention
                                </div>
                                <ChevronRight className="h-4 w-4 text-neutral-600 transition group-hover:text-[color:#dbe6ff]" />
                              </div>
                            </div>

                            <div className="mt-3 flex items-center justify-between gap-3 sm:hidden">
                              <div
                                className={`max-w-full rounded-full border px-3 py-1 text-[11px] font-medium ${getAttentionBadge(
                                  user.stats?.lastCheckInDate
                                )}`}
                              >
                                Needs attention
                              </div>
                              <ChevronRight className="h-4 w-4 shrink-0 text-neutral-600 transition group-hover:text-[color:#dbe6ff]" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </AdminSectionCard>
                </div>

                <div className="mt-6 grid gap-6 lg:mt-8 lg:grid-cols-[1.1fr_0.9fr] [&>*]:min-w-0">
                  <AdminSectionCard title="Attendance Signal">
                    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                      <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-4 sm:p-5">
                        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-500 sm:text-[11px] sm:tracking-[0.28em]">
                          <Users className="h-4 w-4 shrink-0 text-sky-300" />
                          Total base
                        </div>
                        <div className="mt-3 text-2xl font-semibold text-white sm:text-3xl">
                          {data.totalMembers}
                        </div>
                        <p className="mt-2 text-sm text-neutral-400">
                          Current member count in the system.
                        </p>
                      </div>

                      <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-4 sm:p-5">
                        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-500 sm:text-[11px] sm:tracking-[0.28em]">
                          <Activity className="h-4 w-4 shrink-0 text-emerald-300" />
                          Active
                        </div>
                        <div className="mt-3 text-2xl font-semibold text-white sm:text-3xl">
                          {data.activeMembers}
                        </div>
                        <p className="mt-2 text-sm text-neutral-400">
                          Checked in during the last 30 days.
                        </p>
                      </div>

                      <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-4 sm:p-5">
                        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-500 sm:text-[11px] sm:tracking-[0.28em]">
                          <Flame className="h-4 w-4 shrink-0 text-sky-300" />
                          Paid
                        </div>
                        <div className="mt-3 text-2xl font-semibold text-white sm:text-3xl">
                          {data.activeBillingMembers}
                        </div>
                        <p className="mt-2 text-sm text-neutral-400">
                          Members currently covered by active billing.
                        </p>
                      </div>

                      <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-4 sm:p-5">
                        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-500 sm:text-[11px] sm:tracking-[0.28em]">
                          <AlertTriangle className="h-4 w-4 shrink-0 text-red-300" />
                          Billing issue
                        </div>
                        <div className="mt-3 text-2xl font-semibold text-white sm:text-3xl">
                          {data.paymentAttentionMembers}
                        </div>
                        <p className="mt-2 text-sm text-neutral-400">
                          Members with overdue or incomplete billing.
                        </p>
                      </div>
                    </div>
                  </AdminSectionCard>

                  <AdminSectionCard title="Coach Take">
                    <div className="space-y-4">
                      <div className="rounded-2xl border border-[color:rgba(136,156,255,0.12)] bg-[linear-gradient(180deg,rgba(18,23,38,0.64),rgba(10,12,18,0.52))] p-4">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:#aabaff] sm:text-[11px] sm:tracking-[0.28em]">
                          Current read
                        </div>
                        <p className="mt-2 text-sm leading-6 text-neutral-300">
                          Your strongest attendance momentum is sitting with the members at the top
                          of this month&apos;s board, while the attention list shows exactly who may
                          need a nudge before churn becomes a bigger issue.
                        </p>
                      </div>

                      <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-4">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-500 sm:text-[11px] sm:tracking-[0.28em]">
                          Suggested next step
                        </div>
                        <p className="mt-2 text-sm leading-6 text-neutral-400">
                          Next upgrade here should be richer member timelines that combine
                          attendance, performance, and billing state in one admin view.
                        </p>
                      </div>
                    </div>
                  </AdminSectionCard>
                </div>

                <div className="mt-6 lg:mt-8">
                  <AdminSectionCard title="Member Billing Roster">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex flex-wrap gap-2">
                        {[
                          { key: "all", label: "All members" },
                          { key: "paid", label: "Paid" },
                          { key: "payment_issue", label: "Payment issue" },
                          { key: "manual_access", label: "Manual access" },
                          { key: "unpaid", label: "Unpaid" },
                        ].map((option) => (
                          <button
                            key={option.key}
                            type="button"
                            onClick={() =>
                              setMemberFilter(
                                option.key as
                                  | "all"
                                  | "paid"
                                  | "payment_issue"
                                  | "manual_access"
                                  | "unpaid"
                              )
                            }
                            className={[
                              "rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] transition",
                              memberFilter === option.key
                                ? "border-[color:rgba(136,156,255,0.24)] bg-[color:rgba(59,84,170,0.18)] text-[color:#dbe6ff]"
                                : "border-white/10 bg-white/[0.03] text-white/65 hover:bg-white/[0.05]",
                            ].join(" ")}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>

                      <input
                        type="search"
                        value={memberSearch}
                        onChange={(e) => setMemberSearch(e.target.value)}
                        placeholder="Search member by name or email"
                        className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white outline-none transition placeholder:text-neutral-500 focus:border-[color:rgba(136,156,255,0.28)] focus:bg-white/[0.06] lg:max-w-sm"
                      />
                    </div>

                    <div className="mt-5 overflow-hidden rounded-[1.5rem] border border-white/8">
                      <div className="hidden grid-cols-[minmax(0,1.35fr)_150px_130px_130px] gap-4 border-b border-white/8 bg-white/[0.03] px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-500 md:grid">
                        <div>Member</div>
                        <div>Billing</div>
                        <div>Access</div>
                        <div className="text-right">Action</div>
                      </div>

                      {filteredMembers.length === 0 ? (
                        <div className="px-4 py-5 text-sm text-neutral-400">
                          No members match this filter.
                        </div>
                      ) : (
                        <div className="divide-y divide-white/8">
                          {filteredMembers.map((user) => {
                            const billingBadge = getBillingBadge(user);

                            return (
                              <div
                                key={user.id}
                                className="grid gap-4 px-4 py-4 md:grid-cols-[minmax(0,1.35fr)_150px_130px_130px] md:items-center"
                              >
                                <div className="flex min-w-0 items-center gap-3">
                                  <UserAvatar
                                    name={user.name || "Member"}
                                    photoURL={user.photoURL}
                                    size={42}
                                  />
                                  <div className="min-w-0">
                                    <div className="truncate text-sm font-medium text-white sm:text-base">
                                      {user.name || "Unnamed member"}
                                    </div>
                                    <div className="truncate text-xs text-neutral-500 sm:text-sm">
                                      {user.email || "No email"}
                                    </div>
                                  </div>
                                </div>

                                <div>
                                  <span
                                    className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${billingBadge.className}`}
                                  >
                                    {billingBadge.label}
                                  </span>
                                </div>

                                <div className="text-sm text-neutral-300">
                                  {user.appAccessStatus === "limited" || user.appAccessStatus === "full"
                                    ? user.appAccessStatus
                                    : "none"}
                                </div>

                                <div className="md:text-right">
                                  <Link
                                    to={`/admin/performance/${user.id}`}
                                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/80 transition hover:border-[color:rgba(136,156,255,0.20)] hover:bg-white/[0.06]"
                                  >
                                    Open
                                    <ChevronRight className="h-3.5 w-3.5" />
                                  </Link>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </AdminSectionCard>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </AdminOnly>
  );
}
